/**
 * Pure decision behind `Dialog.tsx`/`Sheet.tsx`'s "claim focus on open" step (P0 fix round 4 + P1
 * fix round 5 — see those files' own doc comments for the full incident). Split out here, as a
 * plain function over a minimal duck-typed shape rather than a real `HTMLElement`, so the exact
 * regression (this used to unconditionally return `focusable[0]`, the header's Close button, even
 * when the dialog's own content asked for `autoFocus`) is unit-testable under `node --test` with
 * no DOM — this repo has no jsdom/happy-dom dependency and adds none for this (ponytail).
 */
export interface FocusRootLike {
  querySelector(selectors: string): HTMLElement | null;
}

/**
 * `[autofocus], input, textarea, select` wins over `focusable[0]` whenever one exists — a caller
 * that renders its own focusable field (e.g. `SingerPicker`'s name `TextField`, guest
 * `RequestSheet`'s `SingerNameField`) knows better than "first focusable thing in DOM order",
 * which is usually the header's own Close button. Returns `null` (never the panel) when nothing
 * qualifies — callers fall back to the panel element itself, same as before this was split out.
 */
export function pickInitialFocusTarget(
  panel: FocusRootLike | null,
  focusable: ArrayLike<HTMLElement> | null | undefined
): HTMLElement | null {
  if (!panel) return null;
  const preferred = panel.querySelector('[autofocus], input, textarea, select');
  if (preferred) return preferred;
  return focusable && focusable.length > 0 ? focusable[0] : null;
}
